import React, { useState } from 'react';
import { Card, Row, Col, Progress, Tag } from 'antd';
import { motion } from 'framer-motion';
import { 
  ShoppingCartOutlined, 
  CheckCircleOutlined, 
  SendOutlined, 
  SafetyCertificateOutlined, 
  HomeOutlined 
} from '@ant-design/icons';

interface ProcessStep {
  id: number;
  title: string;
  description: string;
  details: string[];
  icon: React.ReactNode;
  duration: string;
  status: 'completed' | 'active' | 'pending';
}

const processSteps: ProcessStep[] = [
  {
    id: 1,
    title: 'Day1: 收货入仓',
    description: '客户下单，当天收货入仓库',
    details: [
      '客户自送或安排提货',
      '仓库收货验收入仓',
      '当天晚上渠道安排出货',
      '系统生成运单号码'
    ],
    icon: <ShoppingCartOutlined />,
    duration: 'Day1',
    status: 'completed'
  },
  {
    id: 2,
    title: 'Day2: 机场报关',
    description: '普货渠道交国内机场入仓报关',
    details: [
      '普货渠道交国内机场入仓报关',
      '带电货物安排过港代理提货',
      '海关申报单证审核',
      '货物安检查验'
    ],
    icon: <CheckCircleOutlined />,
    duration: 'Day2',
    status: 'completed'
  },
  {
    id: 3,
    title: 'Day3: 国际航班',
    description: '航班飞往LAX/SFO/JFK/ORD',
    details: [
      '普货：航班飞LAX/SFO/JFK/ORD',
      '带电：香港航班飞LAX/SFO/JFK/ORD',
      '货物装载全程跟踪',
      '飞行状态实时更新'
    ],
    icon: <SendOutlined />,
    duration: 'Day3',
    status: 'active'
  },
  {
    id: 4,
    title: 'Day4/5: 到港清关',
    description: '货物到港，目的港清关提货',
    details: [
      'Day4/5 货物安全到港',
      'Day5/6 目的港清关处理',
      'ISF预申报数据提交',
      '清关完成货物提货'
    ],
    icon: <SafetyCertificateOutlined />,
    duration: 'Day4-6',
    status: 'pending'
  },
  {
    id: 5,
    title: 'Day7: 末端派送',
    description: '货物回仓库分货，安排末端配送',
    details: [
      '货物回海外仓库分货',
      '安排卡派或交快递',
      'UPS/FedEx本地配送',
      '签收状态实时跟踪'
    ],
    icon: <HomeOutlined />,
    duration: 'Day7',
    status: 'pending'
  }
];

const ProcessFlow: React.FC = () => {
  const [activeStep, setActiveStep] = useState<number>(1);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);

  const handleStepClick = (stepId: number) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setActiveStep(stepId);
    setTimeout(() => setIsAnimating(false), 500);
  };



  return (
    <div style={{ padding: '24px 0' }}>
      {/* 进度条 */}
      <div style={{ marginBottom: '32px' }}>
        <Progress 
          percent={(activeStep / processSteps.length) * 100} 
          strokeColor="#1890ff"
          trailColor="#f0f0f0"
          strokeWidth={8}
        />
        <div style={{ textAlign: 'center', marginTop: '12px' }}>
          <Tag color="blue">总时效：7-9天自然日提取</Tag>
          <Tag color="green">清关成功率 97.2%</Tag>
          <Tag color="orange">每周3个航班左右</Tag>
        </div>
      </div>

      {/* 水平流程步骤 */}
      <div style={{ position: 'relative' }}>
        <Row gutter={[8, 24]} justify="center">
          {processSteps.map((step, index) => {
            const status = step.id === activeStep ? 'active' : step.status;
            const isActive = step.id === activeStep;
            
            return (
              <div key={step.id} style={{ display: 'contents' }}>
                <Col xs={24} sm={24} md={Math.floor(24/5)} lg={Math.floor(24/5)} xl={Math.floor(24/5)}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    style={{ position: 'relative' }}
                  >
                    <Card
                      hoverable
                      onClick={() => handleStepClick(step.id)}
                      style={{
                        borderColor: isActive ? '#1890ff' : '#d9d9d9',
                        boxShadow: isActive ? '0 4px 12px rgba(24, 144, 255, 0.15)' : '0 2px 8px rgba(0,0,0,0.06)',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        height: '200px',
                        textAlign: 'center'
                      }}
                      bodyStyle={{ 
                        padding: '20px 12px',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                      }}
                    >
                      <div>
                        <div style={{
                          fontSize: '36px',
                          color: isActive ? '#1890ff' : status === 'completed' ? '#52c41a' : '#d9d9d9',
                          marginBottom: '12px'
                        }}>
                          {step.icon}
                        </div>
                        
                        <Tag 
                          color={status === 'completed' ? 'green' : status === 'active' ? 'blue' : 'default'}
                          style={{ marginBottom: '8px' }}
                        >
                          {step.duration}
                        </Tag>
                        
                        <h4 style={{ 
                          margin: '0 0 8px 0', 
                          color: isActive ? '#1890ff' : '#262626',
                          fontSize: '14px',
                          fontWeight: '600'
                        }}>
                          {step.title}
                        </h4>
                        
                        <p style={{ 
                          margin: 0, 
                          color: '#666', 
                          fontSize: '12px',
                          lineHeight: '1.4'
                        }}>
                          {step.description}
                        </p>
                      </div>
                    </Card>
                    
                    {/* 箭头指示器 */}
                    {index < processSteps.length - 1 && (
                      <div className="process-arrow" style={{
                        position: 'absolute',
                        right: '-20px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '20px',
                        color: '#1890ff',
                        zIndex: 10
                      }}>
                        →
                      </div>
                    )}
                  </motion.div>
                </Col>
              </div>
            );
          })}
        </Row>
        
        {/* 详细信息显示区域 */}
        <motion.div
          key={activeStep}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          style={{ marginTop: '32px' }}
        >
          <Card
            style={{
              background: '#f8f9ff',
              border: '1px solid #1890ff',
              borderRadius: '12px'
            }}
            bodyStyle={{ padding: '24px' }}
          >
            <Row gutter={[24, 16]}>
              <Col xs={24} md={8}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '48px', color: '#1890ff', marginBottom: '12px' }}>
                    {processSteps[activeStep - 1].icon}
                  </div>
                  <h3 style={{ color: '#1890ff', margin: 0 }}>
                    {processSteps[activeStep - 1].title}
                  </h3>
                </div>
              </Col>
              <Col xs={24} md={16}>
                <h4 style={{ color: '#262626', marginBottom: '16px' }}>详细流程：</h4>
                <ul style={{ margin: 0, paddingLeft: '16px', color: '#666' }}>
                  {processSteps[activeStep - 1].details.map((detail, idx) => (
                    <motion.li
                      key={idx}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.2, delay: idx * 0.1 }}
                      style={{ marginBottom: '8px', lineHeight: '1.6' }}
                    >
                      {detail}
                    </motion.li>
                  ))}
                </ul>
              </Col>
            </Row>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default ProcessFlow;
